/** @format */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "@/services/authService";
import { tokenUtils, type ConfigurationsResponse } from "@/lib/api/auth.ts";
import { ApiErrorResponse } from "@/api/axiosClient.ts";
import { AUTH_STATUS_KEY, CONFIGURATIONS_KEY } from "@/lib/constants";
import {
  decodeJWT as decodeToken,
  setUserData,
  getUserData,
  loginValidation,
  authStateUtils,
} from "@/utils/auth";

interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
}

interface Tokens {
  access: string;
  refresh: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  tokens: Tokens;
  configurations: ConfigurationsResponse | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  refreshAuthData: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const decodeJWT = (token: string): User | null => {
  try {
    const payload = decodeToken(token);
    if (!payload) return null;

    const userData = payload.user || {};
    const userId = payload.user_id || userData.id;
    return {
      id: userId ? String(userId) : "",
      email: userData.email || "",
      first_name: userData.first_name || "",
      last_name: userData.last_name || "",
      full_name: userData.full_name || "",
    };
  } catch {
    return null;
  }
};

import { Toaster } from "@/components/ui/sonner";
import { toast } from "@/lib/toast";

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const isFetchingConfigurations = useRef(false);

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    const hasToken = !!tokenUtils.getAccessToken();
    const isLoggedIn = localStorage.getItem(AUTH_STATUS_KEY) === "true";
    return hasToken && isLoggedIn;
  });

  const [user, setUser] = useState<User | null>(() => {
    try {
      const userData = getUserData();
      if (userData) {
        return userData;
      }

      const token = tokenUtils.getAccessToken();
      if (token) {
        return decodeJWT(token);
      }

      return null;
    } catch {
      return null;
    }
  });

  const [configurations, setConfigurations] =
    useState<ConfigurationsResponse | null>(() => {
      try {
        const configData = localStorage.getItem(CONFIGURATIONS_KEY);
        if (configData) {
          return JSON.parse(configData);
        }
        return null;
      } catch {
        return null;
      }
    });

  const [tokens, setTokens] = useState<Tokens>(() => ({
    access: tokenUtils.getAccessToken() ?? "",
    refresh: tokenUtils.getRefreshToken() ?? "",
  }));

  const fetchConfigurations = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const configData = await authService.getConfigurations();
      setConfigurations(configData);
      localStorage.setItem(CONFIGURATIONS_KEY, JSON.stringify(configData));
    } catch (error) {
      console.error("Failed to fetch configurations:", error);
    } finally {
      isFetchingConfigurations.current = false;
    }
  }, [isAuthenticated, configurations]);

  const refreshAuth = useCallback(async () => {
    const refreshToken = tokenUtils.getRefreshToken();
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    try {
      const response = await authService.refreshToken(refreshToken);
      tokenUtils.setTokens(response.access, response.refresh);

      const decodedUser = decodeJWT(response.access);
      setUser(decodedUser);
      setIsAuthenticated(true);
      setTokens({ access: response.access, refresh: response.refresh });
      localStorage.setItem(AUTH_STATUS_KEY, "true");
      setUserData(decodedUser);
    } catch (error) {
      authStateUtils.clearAllAuthData();
      setUser(null);
      setIsAuthenticated(false);
      setTokens({ access: "", refresh: "" });
      setConfigurations(null);
      throw error;
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && !configurations) {
      fetchConfigurations();
    }
  }, [isAuthenticated, configurations, fetchConfigurations]);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = tokenUtils.getAccessToken();
      const refreshToken = tokenUtils.getRefreshToken();

      if (!token) {
        setIsAuthenticated(false);
        setUser(null);
        return;
      }

      if (tokenUtils.isTokenExpired(token)) {
        if (refreshToken) {
          try {
            await refreshAuth();
          } catch {}
        } else {
          tokenUtils.clearTokens();
          setIsAuthenticated(false);
          setUser(null);
          setTokens({ access: "", refresh: "" });
          setConfigurations(null);
        }
      } else {
        setIsAuthenticated(true);
        setTokens({ access: token, refresh: refreshToken ?? "" });
        if (!user) {
          const decodedUser = decodeJWT(token);
          setUser(decodedUser);
        }
      }
    };

    checkAuthStatus();
  }, [refreshAuth, user]);

  const refreshAuthData = useCallback(async () => {
    const token = tokenUtils.getAccessToken();
    if (token && tokenUtils.isTokenExpired(token)) {
      await refreshAuth();
    }
    await fetchConfigurations();
  }, [refreshAuth, fetchConfigurations]);

  const login = async (email: string, password: string) => {
    try {
      // Validate login form data
      const validation = loginValidation.validateLoginForm(email, password);
      if (!validation.isValid) {
        const firstError = Object.values(validation.errors)[0];
        throw new Error(firstError);
      }

      // Sanitize login data
      const { email: sanitizedEmail, password: sanitizedPassword } =
        loginValidation.sanitizeLoginData(email, password);

      // Send plain text password - backend handles hashing
      const response = await authService.login({
        email: sanitizedEmail,
        password: sanitizedPassword,
      });

      tokenUtils.setTokens(response.access, response.refresh);

      const decodedUser = decodeJWT(response.access);
      localStorage.setItem(AUTH_STATUS_KEY, "true");
      setUserData(decodedUser);

      setUser(decodedUser);
      setTokens({ access: response.access, refresh: response.refresh });

      isFetchingConfigurations.current = true;
      try {
        const configData = await authService.getConfigurations();
        setConfigurations(configData);
        localStorage.setItem(CONFIGURATIONS_KEY, JSON.stringify(configData));
      } catch (error) {
        console.error("Failed to fetch configurations after login:", error);
      } finally {
        isFetchingConfigurations.current = false;
      }

      setIsAuthenticated(true);
    } catch (error: any) {
      if (error instanceof ApiErrorResponse) {
        if (error.status === 401) {
          throw new Error("Email ose fjalëkalim i pavlefshëm");
        } else if (error.status === 400) {
          const { email, password, non_field_errors } = error.data;
          if (email) throw new Error(email[0]);
          if (password) throw new Error(password[0]);
          if (non_field_errors) throw new Error(non_field_errors[0]);
          throw new Error("Kredenciale të pavlefshme");
        } else if (error.status === 0) {
          throw new Error("Gabim rrjeti. Kontrollo lidhjen.");
        }
        throw new Error(error.message);
      }
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    authStateUtils.clearAllAuthData();
    // Show success message on logout
    toast.success("Dalja u krye me sukses");
    navigate("/login");
  };

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser((prev) => {
      const merged = { ...(prev ?? {}), ...updates } as User;
      setUserData(merged);
      return merged;
    });
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated,
      user,
      tokens,
      configurations,
      login,
      logout,
      refreshAuth,
      refreshAuthData,
      updateUser,
    }),
    [
      isAuthenticated,
      user,
      tokens,
      configurations,
      login,
      logout,
      refreshAuth,
      refreshAuthData,
      updateUser,
    ]
  );

  return React.createElement(
    AuthContext.Provider,
    { value },
    [children as any, React.createElement(Toaster, { key: "toaster" })]
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
